import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Chip, Typography } from '@mui/material';
import { History } from '@mui/icons-material';

import { RecentSearch } from '../recentSearches';
import { RECENT_PARAM } from '../useSearchUrlState';
import { useRecentSearches } from '../useRecentSearches';
import {
  BOOKING_CASE_PARAM,
  mergeBookingCaseContext,
} from '../bookingCaseContext';

interface Props {
  vertical: string;
  /**
   * Cómo se lee esta búsqueda ("Ámsterdam → París · 27 ago · 2 adultos").
   * La trae cada vertical porque solo él sabe qué significan sus parámetros;
   * se resuelve al RENDERIZAR, no al guardar, para que la etiqueta salga en el
   * idioma actual y no en el que estaba cuando se buscó.
   */
  describe: (params: Record<string, string>) => string;
}

/**
 * Las últimas búsquedas del asesor, para volver a una sin retipear.
 *
 * Muestra PARÁMETROS, nunca precios: una card que dijera "desde EUR 90" sería
 * la forma más rápida de que alguien cotice media hora tarde un precio que ya
 * no existe. Al tocar una se navega a su URL y de ahí en adelante manda el
 * cache: si sigue fresca monta al instante, si no se ejecuta.
 */
export function RecentSearches({ vertical, describe }: Props) {
  const { t } = useTranslation();
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const { recents, remove } = useRecentSearches(vertical);

  if (recents.length === 0) return null;

  const visibleRecents = recents
    .map((entry) => ({ entry, label: describe(entry.params).trim() }))
    .filter(({ label }) => Boolean(label));

  if (visibleRecents.length === 0) return null;

  const open = (entry: RecentSearch) => {
    // `RECENT_PARAM` remonta el buscador: sin eso la URL cambia, los resultados
    // se recotizan y el formulario —que se llena al montar— queda en blanco.
    const params = new URLSearchParams(entry.params);
    params.set(RECENT_PARAM, String(entry.savedAt));
    const caseId = new URLSearchParams(search).get(BOOKING_CASE_PARAM);
    navigate(
      `${pathname}${mergeBookingCaseContext(`?${params.toString()}`, caseId)}`,
    );
  };

  return (
    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
      <Typography
        variant="caption"
        color="text.secondary"
        display="flex"
        alignItems="center"
        gap={0.5}
      >
        <History sx={{ fontSize: 16 }} />
        {t('travelServices.recentSearches')}
      </Typography>
      {visibleRecents.map(({ entry, label }) => (
        <Chip
          key={`${entry.savedAt}-${entry.vertical}`}
          size="small"
          variant="outlined"
          label={label}
          onClick={() => open(entry)}
          onDelete={() => remove(entry)}
        />
      ))}
    </Box>
  );
}
